import { useDebouncedValue } from "@mantine/hooks"
import {
  Check,
  Checks,
  Circle,
  Clock,
  DotsThreeVertical,
  MagnifyingGlass,
  Plus,
  X,
} from "@phosphor-icons/react"
import {
  Button,
  CodenameTag,
  Divider,
  InputGroup,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  Tag,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { isEmpty } from "lodash-es"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import CreditAction from "~/components/CreditManagement/Action"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import { TwilioMessages2 } from "~/constants/static.items"
import {
  useCreditWalletListingQuery,
  useCreditWalletSummaryQuery,
} from "~/generated/graphql"
import CreditFilter, { CreditFilterType } from "~/pages/d/credit/CreditFilter"
import { config } from "~/pages/pavti/config"

const Credit = () => {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<CreditFilterType>({})
  const [params, setParams] = useSearchParams()
  const canAdd = useAuthorization(Permission.canAddNewCredit)
  const canApprove = useAuthorization(Permission.canApproveCredit)

  const search = params.get("search")
  const updated = params.get("updated")

  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )

  const page = Number(params.get("page")) || 1

  const [{ data, fetching }, getAllCredits] = useCreditWalletListingQuery({
    variables: {
      limit: config.pageSize,
      offset: (page - 1) * config.pageSize,
      search: debouncedSearch,
      status: filter?.status?.id ?? undefined,
      created_from: filter?.date_from
        ? dayjs(filter?.date_from).startOf("d").format("YYYY-MM-DD")
        : undefined,
      created_to: filter?.date_to
        ? dayjs(filter?.date_to).endOf("d").format("YYYY-MM-DD")
        : undefined,
      verified_by: filter?.verified_by?.id
        ? String(filter?.verified_by?.id)
        : undefined,
    },
    requestPolicy: "cache-and-network",
  })

  const [{ data: creditAggregateData, fetching: summaryFetching }] =
    useCreditWalletSummaryQuery({
      variables: {
        input: {
          approved_by: filter?.verified_by?.id
            ? String(filter?.verified_by?.id)
            : undefined,
          created_from_date: filter?.date_from
            ? dayjs(filter?.date_from).format("YYYY-MM-DD")
            : undefined,
          created_to_date: filter?.date_to
            ? dayjs(filter?.date_to).format("YYYY-MM-DD")
            : undefined,
          status: !filter?.status?.id ? "Active" : filter?.status?.id,
        },
      },
      requestPolicy: "cache-and-network",
      pause: isEmpty(data?.credit_wallet_list),
    })

  const totalCredits = data?.credit_wallet_list?.total_records || 0
  const creditAggregate = isEmpty(data?.credit_wallet_list)
    ? {}
    : creditAggregateData?.credit_wallet_summary_aggregated
  const totalBuyers = creditAggregate?.buyer_count || 0
  const totalCrLimitGranted = creditAggregate?.total_credit_limit
  const totalOutstanding = creditAggregate?.total_outstanding
  const totalCreditUtilized = creditAggregate?.used_credit_hishob_patti
  const totalCrAvailable = creditAggregate?.available_credit_limit
  const avgTotalOverdue = creditAggregate?.avg_overdue_days

  const analyticsColumn = [
    {
      id: "buyers",
      title: "Total No. of Buyers",
      value: totalBuyers,
      valueClassName: "text-blue-300",
      bg: "",
    },
    {
      id: "credit_granted",
      title: "Total Credit Limit Granted",
      value: inr(totalCrLimitGranted || 0),
      valueClassName: "text-blue-300",
      unit: "ha",
    },
    {
      id: "outstanding",
      title: "Total Outstanding / Principal Balance Due",
      value: inr(totalOutstanding || 0),
      valueClassName: "text-red-500",
    },
    {
      id: "credit_utilized",
      title: "Total Exposure /Credit Limit Utilized (H.P)",
      value: inr(totalCreditUtilized || 0),
      valueClassName: "text-blue-300",
    },
    {
      id: "credit_available",
      title: "Total Credit Limit Available",
      value: inr(totalCrAvailable || 0),
      valueClassName: "text-green-400",
    },
    {
      id: "overdue",
      title: "Avg. Total Overdue",
      value: `${avgTotalOverdue} Days`,
      valueClassName: "text-red-500",
    },
  ]

  useEffect(() => {
    getAllCredits({
      requestPolicy: "network-only",
    })
    params.delete("updated")
    setParams(params)
  }, [updated])

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_on",
      Cell({ value }) {
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
      formatter: () => {
        return "Total"
      },
    },

    {
      Header: "Buyer",
      accessor: "buyer",
      id: "buyer",
      Cell({ value, row: { original } }) {
        // if (!value) return "aa"
        return (
          <Cell
            title={
              <Link to={AppRoutes.creditDetail(original?.id)}>
                {value?.name || "aa"}
              </Link>
            }
            subtitle={
              <CodenameTag referenceId={value?.external_reference_id2} />
            }
          />
        )
      },
    },
    {
      Header: "Credit Limit Approved By",
      accessor: "credit_limit_approved_by",
      id: "verified_by",
      Cell({ value, row: { original } }) {
        if (original?.verified_on === null) {
          return "--"
        }
        if (!value) return "--"
        const date = original?.verified_on
        return (
          <Cell
            title={value?.name}
            subtitle={date ? dayjs(date).format("DD MMM 'YY") : ""}
          />
        )
      },
    },

    {
      Header: "Credit Limit Approval Validity",
      accessor: "validity",
      id: "validity",
      Cell({ value, row: { original } }) {
        if (original?.verified_on === null) {
          return "--"
        }
        if (!value) return "--"
        const date = original?.validity_end_date
        return (
          <Cell
            titleClassName="text-brown-400"
            title={value + " Days"}
            subtitle={date ? dayjs(date).format("DD MMM 'YY") : <> </>}
          />
        )
      },
    },
    {
      Header: "Credit Limit",
      accessor: "credit_limit",
      Cell({ value, row: { original } }) {
        if (!value) return "--"
        const cycle = original?.payment_cycle_days
        return (
          <Cell
            titleClassName="font-700 !text-gray-800"
            title={inr(value)}
            subtitle={
              cycle && (
                <Tag variant="outline" intent="info" className="!rounded-xl">
                  {cycle + " Days Cycle"}
                </Tag>
              )
            }
          />
        )
      },
    },
    {
      Header: "Total exposure (actual Exp + shadow exp)",
      accessor: "total_exposure",
      id: "used_amount",
      Cell({ value, row: { original } }) {
        if (original?.verified_on === null) {
          return "--"
        }
        return (
          <Cell
            titleClassName="font-700 !text-blue-300"
            title={inr(value || 0)}
          />
        )
      },
    },
    {
      Header: (
        <div>
          Actual Exposure <br />
          (Final Invoices)
        </div>
      ),
      accessor: "actual_exposure",
      Cell({ value, row: { original } }) {
        if (original?.verified_on === null) {
          return "--"
        }
        return (
          <Cell titleClassName="font-700 !text-red" title={inr(value || 0)} />
        )
      },
    },
    {
      Header: (
        <div>
          Shadow Exposure
          <br />
          (TP + HP without invoice)
        </div>
      ),
      accessor: "shadow_exposure",
      id: "booking_exp",
      Cell({ value, row: { original } }) {
        if (original?.verified_on === null) {
          return "--"
        }
        return (
          <Cell titleClassName="font-700 !text-green" title={inr(value || 0)} />
        )
      },
    },

    {
      Header: "Credit Limit Available",
      accessor: "credit_limit_available",
      Cell({ value }) {
        return (
          <Cell titleClassName="font-700 !text-green-300" title={inr(value)} />
        )
      },
    },

    {
      Header: "Last Transaction Dt.",
      accessor: "last_transaction_date",
      id: "last_tr_date",
      Cell({ value }) {
        return (
          <Cell
            title={<>{value ? dayjs(value).format("DD MMM 'YY") : "--"}</>}
          />
        )
      },
    },

    {
      Header: "Last Transaction Value",
      accessor: "last_transaction_value",
      Cell({ value, row: { original } }) {
        return (
          <Cell
            titleClassName="font-700 text-blue-300"
            title={inr(value || 0)}
          />
        )
      },
    },

    {
      Header: "Overdue Amount",
      accessor: "overdue_amount",
      Cell({ value }) {
        return (
          <Cell titleClassName="font-700 !text-red" title={inr(value || 0)} />
        )
      },
    },

    {
      Header: "Avg Overdue days",
      accessor: "avg_overdue_days",
      Cell({ value }) {
        return (
          <Cell
            title={
              <Tag intent="info" variant="outline" className="!rounded-xl">
                {value || 0}
              </Tag>
            }
          />
        )
      },
    },

    {
      Header: "Deviation Amt. Req.",
      accessor: "deviation_amount_requested",
      Cell({ value }) {
        return <Cell title={value ? inr(value || 0) : "--"} />
      },
    },

    {
      Header: "Deviation O/S Approved",
      accessor: "deviation_os_approved",
      Cell({ value }) {
        return <Cell title={value ? inr(value || 0) : "--"} />
      },
    },

    {
      Header: "Status",
      accessor: "status",
      Cell({ value, row: { original } }) {
        const color = original?.status_metadata?.color
        return (
          <Cell
            titleClassName={classNames(
              "flex gap-1 items-center",
              color ? `!text-${color}` : ""
            )}
            title={
              <>
                <Circle weight="fill" />
                {value}
              </>
            }
          />
        )
      },
    },

    {
      Header: "",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [params, setParams] = useSearchParams()
        const twilioStatus =
          TwilioMessages2[original?.metadata?.current_message_status]

        return (
          <Popover
            minimal
            arrow={false}
            className="p-0"
            trigger="click"
            interactive
            placement="bottom"
            content={
              <Menu className="max-h-72" title="Actions">
                <MenuItem
                  onClick={() => {
                    params.set("action", "true")
                    params.set("id", original?.id)
                    setParams(params)
                  }}
                >
                  Action
                </MenuItem>
                <MenuItem
                  onClick={async () => {
                    const token = localStorage.getItem("token")

                    const number = original?.user?.user[0]?.number
                    if (!number) {
                      toast.error("Buyer has no registered number!", {
                        duration: 3000,
                      })
                      return
                    }

                    const body = {
                      to: number,
                      statusCallback: `https://${
                        import.meta.env.VITE_REST_URL
                      }/rest/trade/status-callback/?credit_id=${original?.id}`,
                      notificationTemplateCodename:
                        "setutrade-credit-intimation-manual",
                      contentVariables: {
                        user_name: original?.user?.user[0]?.name ?? "",
                        sf_code:
                          original?.user?.user[0]?.external_reference_id2 ?? "",
                        credit_limit: inr(original?.total_amount || 0) ?? "",
                        overdue_days:
                          String(
                            original?.extra_data[0]?.avg_overdue_days || 0
                          ) ?? "",
                        date_time:
                          dayjs(new Date()).format("DD MMM YYYY") ?? "",
                        outstand:
                          inr(
                            original?.extra_data[0]?.used_credit_hishob_patti ||
                              0
                          ) ?? "",
                        overdue_amount:
                          inr(original?.overdue_amount || 0) ?? "",
                      },
                    }
                    const data = await fetch(
                      `https://${
                        import.meta.env.VITE_REST_URL
                      }/notification/send_whatsapp_msg/`,
                      {
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        method: "POST",
                        body: JSON.stringify(body),
                      }
                    )
                    toast.success("Whatsapp message sent successfully!", {
                      duration: 3000,
                    })
                  }}
                >
                  <div className="flex justify-between gap-2">
                    <p>Overdue Credit Intimation</p>
                    <div className="flex gap-1">
                      {twilioStatus?.id === "queued" && (
                        <Clock
                          size={20}
                          weight="bold"
                          color={twilioStatus.color}
                        />
                      )}
                      {twilioStatus?.id === "sent" && (
                        <Check
                          size={20}
                          weight="bold"
                          color={twilioStatus.color}
                        />
                      )}
                      {twilioStatus?.id === "delivered" && (
                        <Checks
                          size={20}
                          weight="bold"
                          color={twilioStatus.color}
                        />
                      )}
                      {twilioStatus?.id === "read" && (
                        // use a distinct blue for read (WhatsApp-style blue)
                        <Checks size={20} weight="bold" color="#1877F2" />
                      )}
                      {twilioStatus?.id === "failed" && (
                        <X size={20} weight="bold" color={twilioStatus.color} />
                      )}
                    </div>
                  </div>
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded" hidden={!canApprove}>
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  // const creditData = useMemo(() => {
  //   return (
  //     data?.credit_wallet_list?.results?.map((data, idx) => ({
  //       ...data,
  //       id: idx,
  //       credit_id: data?.id,
  //     })) || []
  //   )
  // }, [data])

  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <div></div>
        <div className="w-[500px] flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            leftElement={<MagnifyingGlass />}
            inputProps={{
              value: search || "",
              placeholder: "Search by Buyer",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
          />
          <CreditFilter filter={filter} setFilter={setFilter} />
        </div>
        <div className="justify-self-end gap-2">
          <Button
            text="Credit"
            variant="outline"
            leftIcon={<Plus />}
            disabled={!canAdd}
            onClick={() => {
              navigate({ pathname: AppRoutes.creditDetail("new") })
            }}
          />
        </div>
      </header>
      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-6 gap-2 p-4 bg-gray-100 rounded-xl">
          {analyticsColumn?.map((col, idx) => {
            return (
              <div key={col?.id + idx} className="flex">
                <div key={col?.id} className="!w-full">
                  <div className="flex flex-col text-center">
                    <div className={classNames("text-sm")}>{col?.title}</div>
                    {
                      <div
                        className={classNames("font-700", col?.valueClassName)}
                      >
                        {summaryFetching || fetching ? (
                          <Spinner />
                        ) : col?.value !== undefined ? (
                          col?.value
                        ) : (
                          "NA"
                        )}
                      </div>
                    }
                  </div>
                </div>

                <div />
                {idx !== analyticsColumn?.length - 1 && <Divider />}
              </div>
            )
          })}
        </div>
      </section>
      <TableView
        relative={false}
        className="!h-[calc(100vh-64px-180px)] !mt-0"
        tableClassName="!mt-0"
        // className="!h-[calc(100vh-64px-57px)]"
        paginationProps={{
          total: Math.ceil(totalCredits / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        isLoading={fetching}
        columns={COLUMNS}
        data={data?.credit_wallet_list?.results || []}
        meta={<>{totalCredits || 0} results</>}
      />
      <CreditAction />
    </>
  )
}

export default Credit
